import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  CloudSync,
  CheckCircle,
  Error,
  Refresh,
  Description,
  Settings,
  Save,
  Science,
  School
} from '@mui/icons-material';
import { apiService } from '../services/api';
import EnhancedFilesTable from './EnhancedFilesTable';

interface LitmosConfigState {
  apiKey: string;
  serverUrl: string;
  source: string;
  isConfigured: boolean;
  lastSync?: string;
  totalCourses?: number;
  litmosCount?: number;
}

interface SyncResult {
  status: string;
  syncedCount?: number;
  errorCount?: number;
  totalCourses?: number;
  message?: string;
}

const LitmosConfig: React.FC = () => {
  const [config, setConfig] = useState<LitmosConfigState>({
    apiKey: '',
    serverUrl: '',
    source: '',
    isConfigured: false
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'success' | 'error'>('unknown');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [uploadResult, setUploadResult] = useState<{ uploaded: number; failed: number; files: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);
  const [totalDocs, setTotalDocs] = useState<number>(0);
  const [polling, setPolling] = useState<boolean>(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any>(null);
  const [progressInterval, setProgressInterval] = useState<any>(null);

  useEffect(() => {
    loadConfiguration();
    loadUploadedDocuments();
  }, []);

  // Poll for status updates while there are active docs
  useEffect(() => {
    let intervalId: any;
    if (polling) {
      intervalId = setInterval(() => {
        loadUploadedDocuments();
      }, 2000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [polling]);

  // Poll for job progress
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  const pollJobProgress = async (jobId: string) => {
    try {
      const response = await apiService.get(`/api/v1/sources/litmos/job/${jobId}`);
      const jobData = response.data;
      
      setJobProgress(jobData);
      
      // If job is completed or failed, stop polling
      if (jobData.status === 'completed' || jobData.status === 'failed') {
        setSyncing(false);
        setCurrentJobId(null);
        
        if (progressInterval) {
          clearInterval(progressInterval);
          setProgressInterval(null);
        }
        
        if (jobData.status === 'completed') {
          setSyncResult({
            status: 'success',
            syncedCount: jobData.result.added + jobData.result.updated,
            errorCount: jobData.result.failed,
            totalCourses: jobData.result.total_courses,
            message: `Synced ${jobData.result.added} new courses, updated ${jobData.result.updated} courses`
          });
          
          setMessage({ 
            type: 'success', 
            text: `✅ Sync completed! ${jobData.result.added} courses added, ${jobData.result.updated} updated` 
          });
          
          // Reload configuration and files list
          setTimeout(() => {
            loadConfiguration();
            loadUploadedDocuments();
            apiService.getDocumentStats().then((stats) => {
              const litmosCount = stats?.by_source?.litmos || 0;
              setConfig((prev) => ({ ...prev, litmosCount, totalCourses: litmosCount }));
            }).catch(() => {});
          }, 1000);
        } else {
          setMessage({ 
            type: 'error', 
            text: `❌ Sync failed: ${jobData.error_message || 'Unknown error'}` 
          });
        }
      }
    } catch (error) {
      console.error('Error polling job progress:', error);
      // Continue polling even if there's an error
    }
  };

  const getErrorMessage = (error: any, fallback: string): string => {
    try {
      const detail = error?.response?.data?.detail;
      if (!detail) {
        return error?.message || fallback;
      }
      if (typeof detail === 'string') {
        return detail;
      }
      if (Array.isArray(detail)) {
        const messages = detail
          .map((d: any) => d?.msg || d?.message || JSON.stringify(d))
          .filter(Boolean);
        return messages.join('; ') || fallback;
      }
      if (typeof detail === 'object') {
        if (detail.msg || detail.message) {
          return detail.msg || detail.message;
        }
        return JSON.stringify(detail);
      }
      return String(detail);
    } catch {
      return fallback;
    }
  }

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/v1/sources/litmos/config');
      const data = response?.data;
      
      if (data) {
        const isConfigured = Boolean(data.api_key && data.server_url && data.enabled);
        
        setConfig((prev) => ({
          ...prev,
          apiKey: data.api_key ? '••••••••' : '',
          serverUrl: data.server_url || '',
          source: data.source || '',
          isConfigured,
          lastSync: data.last_modified
        }));
      }

      // Load document stats to populate counts
      try {
        const stats = await apiService.getDocumentStats();
        const litmosCount = stats?.by_source?.litmos || 0;
        setConfig((prev) => ({ ...prev, litmosCount, totalCourses: litmosCount }));
      } catch {}

      // Load last sync time from recent jobs
      try {
        const jobsResponse = await apiService.get('/api/v1/sources/litmos/jobs?limit=1');
        const jobs = jobsResponse?.data?.jobs || [];
        if (jobs.length > 0 && jobs[0].status === 'completed') {
          setConfig((prev) => ({ ...prev, lastSync: jobs[0].completed_at }));
        }
      } catch {}
    } catch (error) {
      console.error('Failed to load Litmos configuration:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    if (!config.serverUrl) {
      setMessage({ type: 'error', text: 'Please fill in Server URL' });
      return;
    }
    
    // If API key is masked (••••••••), it means we have a saved key, so we can test
    // If API key is empty, user needs to enter it
    if (!config.apiKey || (config.apiKey !== '••••••••' && config.apiKey.trim() === '')) {
      setMessage({ type: 'error', text: 'Please fill in API Key' });
      return;
    }

    try {
      setTesting(true);
      setMessage(null);
      
      const payload = {
        // If API key is masked, don't send it (backend will use saved key)
        // If API key is new/changed, send it
        api_key: config.apiKey === '••••••••' ? undefined : config.apiKey,
        server_url: config.serverUrl,
        source: config.source
      };
      const response = await apiService.post('/api/v1/sources/litmos/test', payload);
      const data = response?.data || {};

      if (data.status === 'success') {
        setConnectionStatus('success');
        setMessage({ 
          type: 'success', 
          text: `✅ Connected successfully! Found ${data.total_courses || 0} courses` 
        });
      } else {
        setConnectionStatus('error');
        setMessage({ 
          type: 'error', 
          text: data.message || 'Connection failed' 
        });
      }
    } catch (error: any) {
      setConnectionStatus('error');
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Connection test failed')
      });
    } finally {
      setTesting(false);
    }
  };

  const saveConfiguration = async () => {
    if (!config.serverUrl) {
      setMessage({ type: 'error', text: 'Please fill in Server URL' });
      return;
    }
    
    if (!config.apiKey || (config.apiKey !== '••••••••' && config.apiKey.trim() === '')) {
      setMessage({ type: 'error', text: 'Please fill in API Key' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const payload = {
        api_key: config.apiKey === '••••••••' ? undefined : config.apiKey,
        server_url: config.serverUrl,
        source: config.source
      };
      const response = await apiService.post('/api/v1/sources/litmos/config', payload);

      if (response.status >= 200 && response.status < 300) {
        setMessage({ type: 'success', text: 'Configuration saved successfully!' });
        setConfig(prev => ({ ...prev, isConfigured: true }));
        
        // Reload configuration to get updated status
        setTimeout(() => {
          loadConfiguration();
          loadUploadedDocuments();
        }, 1000);
      } else {
        const error = response?.data || {};
        setMessage({ 
          type: 'error', 
          text: error.detail || 'Failed to save configuration'
        });
      }
      
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Failed to save configuration')
      });
    } finally {
      setLoading(false);
    }
  };

  const syncCourses = async () => {
    if (!config.isConfigured) {
      setMessage({ type: 'error', text: 'Please configure and test connection first' });
      return;
    }

    try {
      setSyncing(true);
      setMessage(null);
      setSyncResult(null);
      setJobProgress(null);

      // Start the background sync job
      const response = await apiService.post('/api/v1/sources/litmos/sync', {});
      const data = response?.data || {};

      if (response.status >= 200 && response.status < 300 && data.status === 'started') {
        setCurrentJobId(data.job_id);
        setMessage({ 
          type: 'info', 
          text: '🚀 Sync started in background. Progress will update automatically...' 
        });
        
        // Start polling for progress
        const interval = setInterval(() => {
          pollJobProgress(data.job_id);
        }, 1000); // Poll every second
        
        setProgressInterval(interval);
      } else {
        setMessage({ 
          type: 'error', 
          text: data.message || 'Failed to start sync' 
        });
        setSyncing(false);
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Failed to start sync')
      });
      setSyncing(false);
    }
  };

  const handleManualUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      setMessage(null);
      setUploadResult(null);
      setJobProgress(null);

      const formData = new FormData();
      const fileNames: string[] = [];
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
        fileNames.push(files[i].name);
      }
      
      // Add source_type to specify this is from Litmos config page
      formData.append('source_type', 'litmos');

      const response = await apiService.postForm('/api/v1/documents/upload', formData);

      if (response.data.success) {
        const jobId = response.data.job_id;
        
        if (jobId) {
          // Start polling for job progress
          setCurrentJobId(jobId);
          pollJobProgress(jobId);
        } else {
          // Fallback to old behavior if no job_id
          setUploadResult({
            uploaded: response.data.uploaded || fileNames.length,
            failed: response.data.failed || 0,
            files: fileNames
          });
          setMessage({ 
            type: 'success', 
            text: `✅ Successfully uploaded ${response.data.uploaded || fileNames.length} files!` 
          });
          
          setTimeout(() => {
            setUploading(false);
            loadConfiguration();
            loadUploadedDocuments();
            apiService.getDocumentStats().then((stats) => {
              const litmosCount = stats?.by_source?.litmos || 0;
              setConfig((prev) => ({ ...prev, litmosCount, totalCourses: litmosCount }));
            }).catch(() => {});
          }, 1500);
        }
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Upload failed' 
        });
        setUploading(false);
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, 'Upload failed')
      });
    } finally {
      setUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const loadUploadedDocuments = async () => {
    try {
      const res = await apiService.getDocuments({ page: 1, per_page: 25, source_type: 'litmos' });
      setUploadedDocs(res.documents || []);
      setTotalDocs(res.total || 0);
      const hasActive = (res.documents || []).some((d: any) => ['pending','processing'].includes((d.status || '').toString().toLowerCase()));
      setPolling(hasActive);
    } catch (e) {
      // ignore listing errors
    }
  };

  const handleLoadMoreDocuments = async (page: number, perPage: number, search?: string) => {
    const res = await apiService.getDocuments({ 
      page, 
      per_page: perPage, 
      source_type: 'litmos',
      search
    });
    return {
      documents: res.documents || [],
      total: res.total || 0
    };
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <School color="primary" />
          Litmos LMS Configuration
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
          Configure your Litmos Learning Management System integration to access training courses, modules, and assessments.
        </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

        <Box sx={{ display: 'flex', gap: 4, mb: 4, width: '100%', justifyContent: 'space-between', alignItems: 'stretch' }}>
          {/* Left Column - API Configuration */}
          <Card sx={{ flex: 1, minWidth: 420, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Settings />
                API Configuration
              </Typography>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Litmos Server URL"
                  value={config.serverUrl}
                  onChange={(e) => setConfig(prev => ({ ...prev, serverUrl: e.target.value }))}
                  placeholder="https://your-company.litmos.com"
                  helperText="Your Litmos LMS server URL"
                  disabled={loading}
                />
                
                <TextField
                  fullWidth
                  label="API Key"
                  type="password"
                  value={config.apiKey}
                  onChange={(e) => setConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="Enter your Litmos API key"
                  helperText="Your Litmos API authentication key"
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label="Source (Optional)"
                  value={config.source}
                  onChange={(e) => setConfig(prev => ({ ...prev, source: e.target.value }))}
                  placeholder="Company identifier or source name"
                  helperText="Optional source identifier for your organization"
                  disabled={loading}
                />
              </Stack>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={20} /> : <Science />}
                  onClick={testConnection}
                  disabled={testing || loading}
                >
                  Test Connection
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  onClick={saveConfiguration}
                  disabled={loading || testing}
                >
                  Save Configuration
                </Button>

                {connectionStatus === 'success' && (
                  <Chip icon={<CheckCircle />} label="Connected" color="success" size="small" />
                )}
                {connectionStatus === 'error' && (
                  <Chip icon={<Error />} label="Connection Failed" color="error" size="small" />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Right Column - Status Overview */}
          <Card sx={{ flex: 1, minWidth: 420, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Status
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Configuration</Typography>
                  <Chip 
                    label={config.isConfigured ? 'Configured' : 'Not Configured'} 
                    color={config.isConfigured ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Litmos Courses</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    (API synced + Manual uploads)
                  </Typography>
                  <Typography variant="h6" color="primary">{config.litmosCount || 0}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1">Last Sync</Typography>
                  <Typography variant="body2" color="text.secondary">{config.lastSync ? new Date(config.lastSync).toLocaleDateString() : 'Never'}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Course Management Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudSync />
              Course Management
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              Sync your Litmos courses or upload training materials manually to enable search functionality. This will create vector embeddings for intelligent search.
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3, width: '100%', alignItems: 'stretch' }}>
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Litmos Sync
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pull training courses from your Litmos LMS
                </Typography>
                <Button
                  variant="contained"
                  startIcon={syncing ? <CircularProgress size={20} /> : <Refresh />}
                  onClick={syncCourses}
                  disabled={syncing || !config.isConfigured}
                  color="secondary"
                  fullWidth
                >
                  {syncing ? 'Syncing Courses...' : 'Sync Courses Now'}
                </Button>
                
                {/* Real-time Progress Display */}
                {syncing && jobProgress && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sync Progress
                    </Typography>
                    
                    <LinearProgress 
                      variant="determinate" 
                      value={jobProgress.progress.percentage} 
                      sx={{ mb: 1 }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {jobProgress.progress.current} / {jobProgress.progress.total} courses
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {jobProgress.progress.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                    
                    {jobProgress.progress.message && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {jobProgress.progress.message}
                      </Typography>
                    )}
                    
                    {jobProgress.progress.details && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {jobProgress.progress.details.added !== undefined && (
                          <Chip size="small" label={`Added: ${jobProgress.progress.details.added}`} color="success" />
                        )}
                        {jobProgress.progress.details.updated !== undefined && (
                          <Chip size="small" label={`Updated: ${jobProgress.progress.details.updated}`} color="info" />
                        )}
                        {jobProgress.progress.details.failed !== undefined && jobProgress.progress.details.failed > 0 && (
                          <Chip size="small" label={`Failed: ${jobProgress.progress.details.failed}`} color="error" />
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Manual Upload
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload training materials, documents, and videos directly from your computer
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={20} /> : <Description />}
                  onClick={handleManualUpload}
                  disabled={loading || uploading}
                  color="primary"
                  fullWidth
                >
                  {uploading ? 'Uploading...' : 'Upload Training Materials'}
                </Button>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.pptx,.jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.wmv,.flv,.webm,.mkv"
                  style={{ display: 'none' }}
                />
                
                {/* Real-time Upload Progress Display */}
                {uploading && jobProgress && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Upload Progress
                    </Typography>
                    
                    <LinearProgress 
                      variant="determinate" 
                      value={jobProgress.progress.percentage} 
                      sx={{ mb: 1 }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {jobProgress.progress.current} / {jobProgress.progress.total} files
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {jobProgress.progress.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                    
                    {jobProgress.progress.message && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {jobProgress.progress.message}
                      </Typography>
                    )}
                    
                    {jobProgress.progress.details && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {jobProgress.progress.details.succeeded !== undefined && (
                          <Chip size="small" label={`Succeeded: ${jobProgress.progress.details.succeeded}`} color="success" />
                        )}
                        {jobProgress.progress.details.failed !== undefined && jobProgress.progress.details.failed > 0 && (
                          <Chip size="small" label={`Failed: ${jobProgress.progress.details.failed}`} color="error" />
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

          {syncResult && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>Sync Results:</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText 
                    primary={`${syncResult.syncedCount || 0} courses synced successfully`}
                  />
                </ListItem>
                {syncResult.errorCount && syncResult.errorCount > 0 && (
                  <ListItem>
                    <ListItemIcon><Error color="error" /></ListItemIcon>
                    <ListItemText 
                      primary={`${syncResult.errorCount} courses had errors`}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemIcon><Description /></ListItemIcon>
                  <ListItemText 
                    primary={`Total: ${syncResult.totalCourses || 0} courses processed`}
                  />
                </ListItem>
              </List>
            </Paper>
          )}

          {uploadResult && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Upload Results:</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                  <ListItemText 
                    primary={`${uploadResult.uploaded} files uploaded successfully`}
                  />
                </ListItem>
                {uploadResult.failed > 0 && (
                  <ListItem>
                    <ListItemIcon><Error color="error" /></ListItemIcon>
                    <ListItemText 
                      primary={`${uploadResult.failed} files failed to upload`}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemIcon><Description /></ListItemIcon>
                  <ListItemText 
                    primary="Files processed:"
                    secondary={uploadResult.files.join(', ')}
                  />
                </ListItem>
              </List>
            </Paper>
          )}
        </CardContent>
      </Card>

        {/* Results Section */}
        {(syncResult || uploadResult) && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Results
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {syncResult && (
                  <Box sx={{ flex: 1, minWidth: 300 }}>
                    <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Litmos Sync Results
                      </Typography>
                      <List dense>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary={`${syncResult.syncedCount || 0} courses synced successfully`}
                          />
                        </ListItem>
                        {syncResult.errorCount && syncResult.errorCount > 0 && (
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon><Error color="error" /></ListItemIcon>
                            <ListItemText 
                              primary={`${syncResult.errorCount} courses had errors`}
                            />
                          </ListItem>
                        )}
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon><Description /></ListItemIcon>
                          <ListItemText 
                            primary={`Total: ${syncResult.totalCourses || 0} courses processed`}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Box>
                )}

                {uploadResult && (
                  <Box sx={{ flex: 1, minWidth: 300 }}>
                    <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Upload Results
                      </Typography>
                      <List dense>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                          <ListItemText 
                            primary={`${uploadResult.uploaded} files uploaded successfully`}
                          />
                        </ListItem>
                        {uploadResult.failed > 0 && (
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon><Error color="error" /></ListItemIcon>
                            <ListItemText 
                              primary={`${uploadResult.failed} files failed to upload`}
                            />
                          </ListItem>
                        )}
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon><Description /></ListItemIcon>
                          <ListItemText 
                            primary="Files processed:"
                            secondary={uploadResult.files.join(', ')}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Uploaded Files Section */}
        <Box sx={{ mt: 3 }}>
          <EnhancedFilesTable 
            files={uploadedDocs} 
            title="Litmos Training Materials"
            onLoadMore={handleLoadMoreDocuments}
            totalCount={totalDocs}
            serverSidePagination={true}
          />
        </Box>
    </Box>
  );
};

export default LitmosConfig;
